/**
 * Card sync field configuration — THE single source of truth.
 *
 * Defines which SyncCardProps fields trigger dirty detection and timestamp
 * stamping. Both `diffCards()` and `stampChangedCards()` in sync-merge.ts
 * delegate to `hasCardChanged()` below.
 *
 * The list mirrors the syncable subset of `CARD_FIELD_SCOPES` in
 * `field-scopes.ts`. Identity (`id`, `createdTimeISO`) and the per-scope
 * timestamps (`updatedAtISO`, `sharedUpdatedAtISO`, `personalUpdatedAtISO`)
 * are intentionally excluded — they're either immutable or the *output*
 * of dirty detection, not the input.
 *
 * To add a new synced field: add one entry here. That's it.
 */

import type { SyncCardProps } from './shared-types';

// ---------------------------------------------------------------------------
// Field comparison config
// ---------------------------------------------------------------------------

type FieldCompare = { mode: '===' } | { mode: 'length' } | { mode: 'deep' };

type SyncFieldDef = {
  key: keyof SyncCardProps;
  compare: FieldCompare;
};

/**
 * Fields that, when changed, mark a card as dirty for sync.
 *
 * `===` for primitives. `deep` (JSON.stringify equality) for arrays and
 * nested objects — safe here because every value is produced by our own
 * code with stable key order, and per-card payloads are small. `length`
 * is retained as a fast-path option
 */
export const CARD_SYNC_FIELDS: readonly SyncFieldDef[] = [
  { key: 'front', compare: { mode: '===' } },
  { key: 'back', compare: { mode: '===' } },
  { key: 'source', compare: { mode: '===' } },
  { key: 'notes', compare: { mode: '===' } },
  { key: 'sortKey', compare: { mode: '===' } },
  { key: 'starred', compare: { mode: '===' } },
  { key: 'deletedTimeISO', compare: { mode: '===' } },
  { key: 'studyState', compare: { mode: 'deep' } },
  { key: 'actions', compare: { mode: 'length' } },
  { key: 'aiNotes', compare: { mode: 'length' } },
  { key: 'chat', compare: { mode: 'length' } },
  { key: 'images', compare: { mode: 'deep' } },
  { key: 'tags', compare: { mode: 'deep' } },
  { key: 'sourceTimeSec', compare: { mode: '===' } },
  { key: 'sourceTimeEndSec', compare: { mode: '===' } },
  { key: 'joinedDeckAtISO', compare: { mode: '===' } },
  { key: 'undoneISO', compare: { mode: 'deep' } },
] as const;

// ---------------------------------------------------------------------------
// Comparison function
// ---------------------------------------------------------------------------

/**
 * Returns `true` if two cards differ on any synced field.
 * Used by both `diffCards()` and `stampChangedCards()`.
 */
export function hasCardChanged(prev: SyncCardProps, curr: SyncCardProps): boolean {
  // The same object cannot differ from itself. Unchanged cards keep their
  // reference through the store's write path (`cleanCardForStore`,
  // `backfillSortKeys`, a transform's `map`), so this skips the per-field
  // `JSON.stringify` for every untouched card of a deck-wide transform.
  if (prev === curr) return false;
  for (const { key, compare } of CARD_SYNC_FIELDS) {
    if (compare.mode === '===') {
      if (prev[key] !== curr[key]) return true;
    } else if (compare.mode === 'length') {
      const a = prev[key] as unknown[] | undefined;
      const b = curr[key] as unknown[] | undefined;
      if (a?.length !== b?.length) return true;
    } else if (compare.mode === 'deep') {
      if (JSON.stringify(prev[key]) !== JSON.stringify(curr[key])) return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Legacy shape coercion
// ---------------------------------------------------------------------------

/**
 * Text fields the sync schema types as `z.string()` but that legacy cards
 * (migrated from the old Supabase shape) sometimes stored as an ARRAY —
 * notably `notes`, which defaulted to `[]` before it became a string. An
 * array value fails `z.string()` validation on `cards-bulk-push`
 * ("Expected string, received array"), which jams the whole push. Coerce
 * array → string defensively at the schema boundary so the legacy shape is
 * accepted and normalized.
 */
export const LEGACY_STRING_CARD_FIELDS = [
  'front',
  'back',
  'source',
  'notes',
  'sortKey',
] as const;

/** Coerce a legacy array value to a string (joins string members; `[]` → ""). */
export function coerceLegacyCardString(v: unknown): unknown {
  if (!Array.isArray(v)) return v;
  return v.filter((x): x is string => typeof x === 'string').join('\n');
}
